// Localized lookup for team region (city) and name (nickname) strings.
//
// Per I18N_WORKFLOW.md §3:
//   - `region` (from common/teamInfos.ts) is the city; translated via `teamRegion.*`.
//   - NBA short display names (Hawks, Celtics, Warriors, …) are translated via
//     `teamName.*`. Abbrevs like ATL/BOS/GSW are data/route/logo identifiers.
//     Keep them for URLs, logos, sort/search values, and external data matching,
//     but user-facing display should use a localized short team name whenever
//     the team can be resolved.
//     Non-NBA fictional names (Thunderbirds, Glaciers, …) have no entry in the
//     namespace and pass through unchanged (defaultValue fallback).
//   - Real-player real-team names are a separate concern (see future
//     `realTeam.*` namespace + real-player name mapping table).
import i18n from "./i18n.ts";

export const translateTeamRegion = (region: string): string => {
	if (!i18n.isInitialized) return region;
	const t = i18n.t(`teamRegion.${region}`, { ns: "ui", defaultValue: region });
	return typeof t === "string" && t.length > 0 ? t : region;
};

export const translateTeamName = (name: string): string => {
	if (!i18n.isInitialized) return name;
	const t = i18n.t(`teamName.${name}`, { ns: "ui", defaultValue: name });
	return typeof t === "string" && t.length > 0 ? t : name;
};

export const translateTeamFullName = (
	fullName: string | null | undefined,
): string => {
	if (!fullName) return "";
	if (!i18n.isInitialized) return fullName;

	const parts = fullName.split(" ");
	let best:
		| {
				score: number;
				text: string;
		  }
		| undefined;

	for (let i = 1; i < parts.length; i += 1) {
		const region = parts.slice(0, i).join(" ");
		const name = parts.slice(i).join(" ");
		const translatedRegion = translateTeamRegion(region);
		const translatedName = translateTeamName(name);
		const score =
			(translatedRegion === region ? 0 : 1) + (translatedName === name ? 0 : 1);

		if (score > (best?.score ?? 0)) {
			best = {
				score,
				text: i18n.language.startsWith("zh")
					? `${translatedRegion}${translatedName}`
					: `${translatedRegion} ${translatedName}`,
			};
		}
	}

	return best?.text ?? fullName;
};

export const translateTeamShortName = (
	team:
		| {
				abbrev?: string;
				name?: string;
		  }
		| undefined,
	fallback?: string,
): string => {
	if (team?.name) {
		return translateTeamName(team.name);
	}

	return team?.abbrev ?? fallback ?? "";
};
